import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, formatApiErrorDetail } from "./api";

const NEXT_STATUS = ["new", "in_review", "contacted", "closed"];
const LABEL = { new: "New", in_review: "In review", contacted: "Contacted", closed: "Closed" };

export default function EnquiryDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    adminApi.getEnquiry(id).then((r) => setItem(r.data));
  }, [id]);

  const patch = async (body) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await adminApi.patchEnquiry(id, body);
      setItem(data);
      setNote("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(item.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  if (!item) return <div className="ga-page">Loading…</div>;

  return (
    <div className="ga-page" data-testid="enquiry-detail">
      <div className="ga-crumbs">
        <Link to="/admin/enquiries">Enquiries</Link>
        <span> / </span>
        <span>{item.name}</span>
      </div>

      <div className="ga-detail__head">
        <div>
          <h1 className="ga-title" data-testid="detail-name">{item.name}</h1>
          <div className="ga-mute">{item.company || "—"} · {item.category}</div>
        </div>
        <div className="ga-detail__status">
          <label className="ga-mute" htmlFor="status-select">Status</label>
          <select
            id="status-select"
            value={item.status}
            onChange={(e) => patch({ status: e.target.value })}
            disabled={busy}
            data-testid="status-select"
          >
            {NEXT_STATUS.map((s) => (
              <option key={s} value={s}>{LABEL[s]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="ga-detail__grid">
        <div className="ga-detail__field">
          <div className="ga-mute">Email</div>
          <div className="ga-detail__row">
            <a href={`mailto:${item.email}`} data-testid="detail-email">{item.email}</a>
            <button type="button" className="ga-btn ga-btn--ghost" onClick={copyEmail} data-testid="copy-email">
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="ga-detail__field">
          <div className="ga-mute">Phone</div>
          <div>
            {item.phone ? <a href={`tel:${item.phone.replace(/\s+/g, "")}`}>{item.phone}</a> : "—"}
          </div>
        </div>
        <div className="ga-detail__field">
          <div className="ga-mute">Received</div>
          <div>{new Date(item.created_at).toLocaleString()}</div>
        </div>
        <div className="ga-detail__field" data-testid="detail-source">
          <div className="ga-mute">Source</div>
          <div>
            {(() => {
              const s = item.source;
              if (!s || s === "contact_form") return "Contact form";
              if (s === "website_assistant") return "Website assistant";
              return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
            })()}
          </div>
        </div>
      </div>

      <div className="ga-detail__msg" data-testid="detail-message">
        <div className="ga-mute">Message</div>
        <pre>{item.message}</pre>
      </div>

      <div className="ga-notes">
        <div className="ga-eyebrow">Notes</div>
        {(item.notes || []).length === 0 && <div className="ga-mute">No notes yet.</div>}
        {(item.notes || []).map((n, i) => (
          <div className="ga-note" key={i}>
            <div className="ga-mute">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
            <div>{n.text}</div>
          </div>
        ))}

        <form
          className="ga-note-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (note.trim()) patch({ note: note.trim() });
          }}
        >
          <textarea
            rows={3}
            placeholder="Add an internal note (not visible to sender)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="note-input"
          />
          <div className="ga-note-form__row">
            {err && <div className="ga-error">{err}</div>}
            <button type="submit" className="ga-btn" disabled={busy || !note.trim()} data-testid="note-submit">
              Add note
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
